import { AlertTriangle, Pencil, Trash2 } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Liability } from '@/hooks/useLiabilities';

interface LiabilityMobileCardProps {
  liability: Liability;
  onEdit: (liability: Liability) => void;
  onDelete: (id: string) => void;
}

export function LiabilityMobileCard({ liability, onEdit, onDelete }: LiabilityMobileCardProps) {
  const paidPct =
    Number(liability.amount) > 0
      ? ((Number(liability.amount) - Number(liability.remaining_balance)) / Number(liability.amount)) * 100
      : 0;

  const typeLabel = liability.type
    .replace('_', ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());

  const formattedBalance = Number(liability.remaining_balance).toLocaleString(undefined, {
    style: 'currency',
    currency: liability.currency,
  });

  return (
    <Card className="w-full">
      <CardContent className="p-4 space-y-2">
        {/* Top row: name + AlertTriangle icon + status badge */}
        <div className="flex items-center justify-between gap-2">
          <span className="font-semibold text-sm flex items-center gap-1.5 truncate">
            <AlertTriangle className="h-4 w-4 text-amber-500 shrink-0" />
            {liability.name}
          </span>
          <Badge variant={liability.status === 'active' ? 'destructive' : 'default'} className="shrink-0">
            {liability.status === 'active' ? 'Active' : 'Paid Off'}
          </Badge>
        </div>

        {/* Middle row: type badge + remaining balance */}
        <div className="flex items-center justify-between gap-2">
          <Badge variant="outline" className="text-xs">
            {typeLabel}
          </Badge>
          <span className="font-mono text-sm font-semibold">{formattedBalance}</span>
        </div>

        {/* Bottom row: repayment progress bar + edit/delete buttons */}
        <div className="flex items-center justify-between gap-2">
          <Progress value={Math.min(paidPct, 100)} className="flex-1 h-2" />
          <div className="flex gap-1 shrink-0">
            <Button
              variant="ghost"
              size="icon"
              className="min-h-[44px] min-w-[44px]"
              onClick={() => onEdit(liability)}
              aria-label="Edit liability"
            >
              <Pencil className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="min-h-[44px] min-w-[44px]"
              onClick={() => onDelete(liability.id)}
              aria-label="Delete liability"
            >
              <Trash2 className="h-4 w-4 text-destructive" />
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
